import { Bodies, type Body, type CollisionEvent, type World } from "fast-2d";

type Point = [number, number];

export interface FractureOptions {
	rng: () => number;
	maxFragments?: number;
	minArea?: number;
	minEdge?: number;
	minThickness?: number;
	impactInset?: number;
	shrink?: number;
	impulseScale?: number;
	maxShardSpeed?: number;
	densityScale?: number;
}

const DEFAULT_MIN_AREA = 0.035;
const DEFAULT_MIN_EDGE = 0.12;
const DEFAULT_MAX_FRAGMENTS = 8;
const DEFAULT_IMPACT_INSET = 0.24;
const DEFAULT_SHRINK = 0.975;

export function collisionImpactPoint(event: CollisionEvent): Point {
	if (event.manifold.pointCount === 0) {
		return [
			(event.bodyA.position.x + event.bodyB.position.x) * 0.5,
			(event.bodyA.position.y + event.bodyB.position.y) * 0.5,
		];
	}
	let x = 0;
	let y = 0;
	for (let i = 0; i < event.manifold.pointCount; i++) {
		const point = event.manifold.points[i]!.point;
		x += point.x;
		y += point.y;
	}
	const count = event.manifold.pointCount;
	return [x / count, y / count];
}

export function fractureBody(
	world: World,
	body: Body,
	impactPoint: Point,
	strength: number,
	options: FractureOptions,
): Body[] {
	if (body.isCircle || body.vertexCount < 3 || !world.bodies.includes(body)) return [];

	body.synchronize();
	const source = worldVertices(body);
	if (Math.abs(signedArea(source)) < DEFAULT_MIN_AREA) return [];

	const parentCentroid = polygonCentroid(source);
	const inset = options.impactInset ?? DEFAULT_IMPACT_INSET;
	const focus: Point = [
		impactPoint[0] * (1 - inset) + parentCentroid[0] * inset,
		impactPoint[1] * (1 - inset) + parentCentroid[1] * inset,
	];

	const minArea = options.minArea ?? DEFAULT_MIN_AREA;
	const minEdge = options.minEdge ?? DEFAULT_MIN_EDGE;
	const minThickness = options.minThickness ?? minEdge * 0.8;
	const maxFragments = Math.max(2, Math.floor(options.maxFragments ?? DEFAULT_MAX_FRAGMENTS));
	const targetFragments = Math.min(
		maxFragments,
		Math.max(3, Math.floor(3 + Math.min(strength, 80) * 0.09 + options.rng() * 2)),
	);

	let pieces = fanTriangulate(source, focus).filter((piece) => isUsable(piece, minArea, minEdge, minThickness));
	pieces = splitLargestPieces(pieces, targetFragments, minArea, minEdge, minThickness, options.rng);
	if (pieces.length < 2) return [];

	world.remove(body);

	const shrink = options.shrink ?? DEFAULT_SHRINK;
	const impulseScale = options.impulseScale ?? 0.06;
	const maxShardSpeed = options.maxShardSpeed ?? 34;
	const parentVx = body.isStatic ? 0 : body.velocity.x;
	const parentVy = body.isStatic ? 0 : body.velocity.y;
	const parentAngular = body.isStatic ? 0 : body.angularVelocity;
	const parentDensity = Math.max(0.1, body.density * (options.densityScale ?? 0.92));
	let maxDistance = 0;
	for (const piece of pieces) {
		const center = polygonCentroid(piece);
		maxDistance = Math.max(maxDistance, Math.hypot(center[0] - impactPoint[0], center[1] - impactPoint[1]));
	}
	maxDistance = Math.max(maxDistance, 0.001);

	const shards: Body[] = [];
	for (const piece of pieces) {
		const shrunken = shrinkPolygon(piece, shrink);
		if (!isUsable(shrunken, minArea * 0.65, minEdge * 0.75, minThickness * 0.7)) continue;
		const center = polygonCentroid(shrunken);
		const local = shrunken.map(([x, y]) => [x - center[0], y - center[1]] as [number, number]);
		if (signedArea(local) < 0) local.reverse();

		let dx = center[0] - impactPoint[0];
		let dy = center[1] - impactPoint[1];
		let len = Math.hypot(dx, dy);
		if (len < 0.001) {
			dx = center[0] - parentCentroid[0];
			dy = center[1] - parentCentroid[1];
			len = Math.hypot(dx, dy) || 1;
		}
		const nx = dx / len;
		const ny = dy / len;
		const distanceBoost = 0.65 + (len / maxDistance) * 0.55;
		const kick = Math.min(maxShardSpeed, Math.max(1.8, strength * impulseScale * distanceBoost));
		const tangent = (options.rng() - 0.5) * kick * 0.16;
		const vx = clamp(parentVx + nx * kick - ny * tangent, -maxShardSpeed, maxShardSpeed);
		const vy = clamp(parentVy + ny * kick + nx * tangent, -maxShardSpeed, maxShardSpeed);
		const angularKick = clamp((options.rng() - 0.5) * strength * 0.11 + nx * 2.2 - ny * 1.4, -22, 22);

		shards.push(
			world.add(
				Bodies.polygon({
					position: center,
					vertices: local,
					density: parentDensity,
					friction: body.friction,
					restitution: Math.min(0.08, body.restitution + 0.025),
					linearDamping: Math.max(body.linearDamping, 0.025),
					angularDamping: Math.max(body.angularDamping, 0.018),
					velocity: [vx, vy],
					angularVelocity: clamp(parentAngular + angularKick, -28, 28),
					filter: {
						category: body.filterCategory,
						mask: body.filterMask,
						group: body.filterGroup,
					},
				}),
			),
		);
	}

	return shards;
}

function worldVertices(body: Body): Point[] {
	const vertices: Point[] = [];
	for (let i = 0; i < body.worldVerts.length; i += 2) {
		vertices.push([body.worldVerts[i]!, body.worldVerts[i + 1]!]);
	}
	if (signedArea(vertices) < 0) vertices.reverse();
	return vertices;
}

function fanTriangulate(vertices: readonly Point[], focus: Point): Point[][] {
	const pieces: Point[][] = [];
	for (let i = 0; i < vertices.length; i++) {
		const a = vertices[i]!;
		const b = vertices[(i + 1) % vertices.length]!;
		const piece: Point[] = [focus, a, b];
		if (signedArea(piece) < 0) piece.reverse();
		pieces.push(piece);
	}
	return pieces;
}

function splitLargestPieces(
	pieces: Point[][],
	target: number,
	minArea: number,
	minEdge: number,
	minThickness: number,
	random: () => number,
): Point[][] {
	while (pieces.length < target) {
		let bestIndex = -1;
		let bestArea = 0;
		for (let i = 0; i < pieces.length; i++) {
			const area = Math.abs(signedArea(pieces[i]!));
			if (area > bestArea) {
				bestArea = area;
				bestIndex = i;
			}
		}
		if (bestIndex < 0 || bestArea < minArea * 2.35) break;
		const split = splitTriangle(pieces[bestIndex]!, random);
		if (!split || !isUsable(split[0], minArea, minEdge, minThickness) || !isUsable(split[1], minArea, minEdge, minThickness)) break;
		pieces.splice(bestIndex, 1, split[0], split[1]);
	}
	return pieces;
}

function splitTriangle(triangle: Point[], random: () => number): [Point[], Point[]] | null {
	if (triangle.length !== 3) return null;
	let edgeStart = 0;
	let bestLenSq = 0;
	for (let i = 0; i < 3; i++) {
		const a = triangle[i]!;
		const b = triangle[(i + 1) % 3]!;
		const dx = b[0] - a[0];
		const dy = b[1] - a[1];
		const lenSq = dx * dx + dy * dy;
		if (lenSq > bestLenSq) {
			bestLenSq = lenSq;
			edgeStart = i;
		}
	}
	const a = triangle[edgeStart]!;
	const b = triangle[(edgeStart + 1) % 3]!;
	const c = triangle[(edgeStart + 2) % 3]!;
	const t = 0.42 + random() * 0.16;
	const mid: Point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
	const first = orient([c, a, mid]);
	const second = orient([c, mid, b]);
	return [first, second];
}

function shrinkPolygon(vertices: readonly Point[], amount: number): Point[] {
	const center = polygonCentroid(vertices);
	return vertices.map(([x, y]) => [center[0] + (x - center[0]) * amount, center[1] + (y - center[1]) * amount]);
}

function polygonCentroid(vertices: readonly Point[]): Point {
	let area2 = 0;
	let cx = 0;
	let cy = 0;
	for (let i = 0; i < vertices.length; i++) {
		const a = vertices[i]!;
		const b = vertices[(i + 1) % vertices.length]!;
		const cross = a[0] * b[1] - b[0] * a[1];
		area2 += cross;
		cx += (a[0] + b[0]) * cross;
		cy += (a[1] + b[1]) * cross;
	}
	if (Math.abs(area2) < 1e-9) {
		let x = 0;
		let y = 0;
		for (const vertex of vertices) {
			x += vertex[0];
			y += vertex[1];
		}
		return [x / vertices.length, y / vertices.length];
	}
	return [cx / (3 * area2), cy / (3 * area2)];
}

function signedArea(vertices: readonly Point[]): number {
	let area = 0;
	for (let i = 0; i < vertices.length; i++) {
		const a = vertices[i]!;
		const b = vertices[(i + 1) % vertices.length]!;
		area += a[0] * b[1] - b[0] * a[1];
	}
	return area * 0.5;
}

function isUsable(vertices: readonly Point[], minArea: number, minEdge: number, minThickness: number): boolean {
	const area = Math.abs(signedArea(vertices));
	if (vertices.length < 3 || area < minArea) return false;
	const minEdgeSq = minEdge * minEdge;
	let maxEdge = 0;
	for (let i = 0; i < vertices.length; i++) {
		const a = vertices[i]!;
		const b = vertices[(i + 1) % vertices.length]!;
		const dx = b[0] - a[0];
		const dy = b[1] - a[1];
		const edgeSq = dx * dx + dy * dy;
		if (edgeSq < minEdgeSq) return false;
		maxEdge = Math.max(maxEdge, Math.sqrt(edgeSq));
	}
	if (maxEdge > 0 && (area * 2) / maxEdge < minThickness) return false;
	return true;
}

function orient(vertices: Point[]): Point[] {
	if (signedArea(vertices) < 0) vertices.reverse();
	return vertices;
}

function clamp(value: number, min: number, max: number): number {
	return value < min ? min : value > max ? max : value;
}